import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "@/redux/root-reducer";

export const getPage = (state: RootState) => state.contentHub.page;
export const getPageNotFound = (state: RootState) =>
  state.contentHub.page === null && state.contentHub.pageNotFound;

export const getPageSavingStatus = (state: RootState) =>
  state.contentHub.saveTransaction.status;

export const getHasUnsavedChanges = createSelector(
  (state: RootState) => state.contentHub.editor,
  (editor) => {
    const { hasNonEditorUnsavedChanges, currentChangeIndex } = editor;

    const hasUnsavedChanges =
      hasNonEditorUnsavedChanges || currentChangeIndex !== 0;

    return hasUnsavedChanges;
  }
);

export const getPageContent = (state: RootState) =>
  state.contentHub.page?.content;

export const getIsReadonly = (state: RootState) => state.contentHub.readonly;
export const getEditorEnabled = (state: RootState) =>
  state.contentHub.editor.enabled;
export const getSelectedContextMenuTab = (state: RootState) =>
  state.contentHub.editor.selectedContextMenuTab;
export const getSharingSettingsOpen = (state: RootState) =>
  state.contentHub.sharingSettings.isOpen;
export const getOrganizationExternalId = (state: RootState) =>
  state.contentHub.organizationExternalId;
export const getSpaceExternalId = (state: RootState) =>
  state.contentHub.spaceExternalId;
export const getSpacesEnabled = (state: RootState) =>
  state.contentHub.spacesEnabled;
export const getContentApiAuthToken = (state: RootState) =>
  state.contentHub.authentication.contentApiAuthToken;
export const getDefaultAuthToken = (state: RootState) =>
  state.contentHub.authentication.defaultAuthToken;
export const getEventSummariesAuthToken = (pageSlug?: string) => (
  state: RootState
) =>
  pageSlug
    ? state.contentHub.authentication.contentApiAuthToken
    : state.contentHub.authentication.defaultAuthToken;
export const getContentApiEndpoint = (state: RootState) =>
  state.contentHub.authentication.contentApiEndpoint;
export const getIsBlockSelectionPanelOpen = (state: RootState) =>
  state.contentHub.blockSelectionPanel?.isOpen ?? false;
export const getSelectedBlockCategory = (state: RootState) =>
  state.contentHub.blockSelectionPanel?.selectedBlockCategory ?? null;

export const getPageEditorLoaded = (state: RootState) => {
  if (state.contentHub.authentication.status !== "authenticated") return false;

  return !!state.contentHub.page;
};

export const getPageTitle = ({ contentHub }: RootState) =>
  contentHub.page?.metadata?.title ?? "";

export const getPageDescription = ({ contentHub }: RootState) =>
  contentHub.page?.metadata?.description ?? "";

export const getPageUrl = ({ contentHub }: RootState) =>
  contentHub.page?.pageUrl ?? "";

export const getPageSlug = ({ contentHub }: RootState) =>
  contentHub.page?.slug ?? "";

export const getSelectedColorPreset = ({ contentHub }: RootState) =>
  contentHub.page?.brandingPreset?.config ?? null;

export const getSelectedThemeKind = ({ contentHub }: RootState) =>
  contentHub.page?.themeKind ?? null;

export const getSlugValidation = ({ contentHub }: RootState) =>
  contentHub.sharingSettings.slugValidation;

export const getSharingImageMetadata = ({ contentHub }: RootState) =>
  contentHub.page?.metadata?.image ?? null;

export const getMediaUploadForTransaction = (uploadTransactionId: string) => ({
  contentHub,
}: RootState) => contentHub.mediaUploads[uploadTransactionId] ?? null;

export const getGatingSettingsOpen = (state: RootState) =>
  state.contentHub.gatingSettingsOpen;

export const getLinkedEventSettingsOpen = (state: RootState) =>
  state.contentHub.linkedEventSettingsOpen;

export const getLinkedEventSettingsSetupRequest = (state: RootState) =>
  state.contentHub.linkedEventSettingsSetupRequest;

export const getContentGate = (state: RootState) =>
  state.contentHub.contentGate;

export const getAnalyticsEventStatus = (state: RootState) =>
  state.contentHub.analyticsEvent;

export const getAllRecordings = (state: RootState) =>
  Object.values(state.contentHub.recordings);

export const getRecordingById = (recordingId: string) => (state: RootState) =>
  state.contentHub.recordings[recordingId];

export const getEventSummariesStatus = (state: RootState) =>
  state.contentHub.eventSummaries.status;

export const getAllEventSummaries = (state: RootState) =>
  Object.values(state.contentHub.eventSummaries.items);

export const getEventSummaryById = (eventExternalId: string) => (
  state: RootState
) => state.contentHub.eventSummaries.items[eventExternalId];

export const getEventSummariesByIds = (eventExternalIds: string[]) =>
  createSelector(
    (state: RootState) => state.contentHub.eventSummaries.items,
    (eventSummaries) =>
      eventExternalIds
        .map((eventExternalId) => eventSummaries[eventExternalId])
        .filter(Boolean)
  );

export const getGatingType = (state: RootState) =>
  state.contentHub.page?.gatingType ?? null;

export const getSavedPublishedState = (state: RootState) =>
  state.contentHub.pagePublished;

export const getConfig = (state: RootState) => state.contentHub.config;
